/**
 * Genderless blob characters: a procedural capsule body + sphere head + two
 * small hand spheres, seated around the table. Each blob does a sine-wave idle
 * bob; on win/lose the blob plays a reaction tween (jump / slump). Driven by the
 * roster (presence) + resolution (win/lose poses). ~3 meshes per blob; at 15
 * players that's ~45 draw calls — within budget alongside the instanced
 * cards/chips. Blobs are deliberately simple, not 52-model heavy.
 */

import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import type { RosterEntry } from '@blackjack/shared';
import { computeSeats, assignSeatIndices, CROUPIER, PLAYER_SEAT_COUNT, type Seat } from './seating';
import { useGameStore } from '../state/store';
import { palette, fonts } from '../theme/tokens';

type Reaction = 'idle' | 'win' | 'lose';

/**
 * Vivid, mutually-distinct player colors (casino chip / felt-friendly hues),
 * assigned by seat-occupancy index so neighbours never share a color. 11 entries
 * = one per painted betting box.
 */
const PLAYER_COLORS = [
  '#d8443c', // red
  '#3b82d6', // blue
  '#e7c466', // gold
  '#7b3fb8', // purple
  '#2b9d8f', // teal
  '#e07a3a', // orange
  '#46a758', // green
  '#d6519e', // pink
  '#5b8def', // periwinkle
  '#b8902f', // brass
  '#9a4bd8', // violet
] as const;

/** Tuxedo palette for the croupier. */
const TUX = {
  jacket: '#15161a', // near-black charcoal
  shirt: '#f2efe6', // off-white dress shirt
  bowtie: '#08080a', // black bowtie (reads against the white shirt)
  head: '#9aa0a6', // neutral gray head so the dark jacket reads as a tuxedo
  gloves: '#f4f1e8', // white dealer gloves
} as const;

/** How high the croupier floats so its body clears the table top (TABLE.topY). */
const CROUPIER_LIFT = 0.65;

interface BlobProps {
  readonly seat: Seat | typeof CROUPIER;
  readonly name: string;
  readonly isCroupier: boolean;
  readonly reaction: Reaction;
  readonly phaseOffset: number;
  /** Body color (players); the croupier ignores this and wears the tuxedo. */
  readonly color: string;
}

function Blob({ seat, name, isCroupier, reaction, phaseOffset, color }: BlobProps) {
  const groupRef = useRef<THREE.Group>(null);
  const bodyColor = useMemo(() => new THREE.Color(isCroupier ? TUX.jacket : color), [isCroupier, color]);

  // The croupier faces the PLAYERS / camera (+z), so its tux front + bowtie read.
  // (CROUPIER.facing = PI turned its front toward the back wall — fine for a
  // symmetric blob, wrong once it has a front.) Players keep their inward facing.
  const facing = isCroupier ? 0 : 'facing' in seat ? seat.facing : 0;
  const lift = isCroupier ? CROUPIER_LIFT : 0;

  useFrame((state) => {
    const g = groupRef.current;
    if (!g) return;
    const t = state.clock.elapsedTime;
    // Idle sine bob.
    const idleBob = Math.sin(t * 1.6 + phaseOffset) * 0.04;
    let extraY = 0;
    let leanX = 0;
    if (reaction === 'win') {
      extraY = Math.abs(Math.sin(t * 6)) * 0.22; // excited hop
    } else if (reaction === 'lose') {
      extraY = -0.12;
      leanX = 0.18; // slump forward
    }
    g.position.y = idleBob + extraY;
    g.rotation.x = THREE.MathUtils.lerp(g.rotation.x, leanX, 0.1);
  });

  return (
    <group position={[seat.floor.x, lift, seat.floor.z]} rotation={[0, facing, 0]}>
      <group ref={groupRef} position={[0, 0, 0]}>
        {/* Body (capsule). For the croupier this is the tuxedo jacket. */}
        <mesh castShadow position={[0, 0.55, 0]}>
          <capsuleGeometry args={[0.28, 0.5, 8, 16]} />
          <meshStandardMaterial color={bodyColor} roughness={isCroupier ? 0.45 : 0.7} metalness={0.05} />
        </mesh>
        {/* Head (sphere). The croupier gets a gray head so the dark tuxedo reads
            as a jacket rather than merging into a single black blob. */}
        <mesh castShadow position={[0, 1.12, 0]}>
          <sphereGeometry args={[0.22, 24, 24]} />
          <meshStandardMaterial color={isCroupier ? TUX.head : bodyColor} roughness={0.6} metalness={0.05} />
        </mesh>
        {/* Hands / arms. Players: one sphere each. Croupier: a two-tone arm — a
            black tuxedo SLEEVE sphere (upper, toward the shoulder) ending in a
            white GLOVE sphere (lower), so each arm reads as half-jacket/half-glove. */}
        {isCroupier ? (
          <>
            {/* Arms reach FORWARD over the table (dealer pose): sleeve near the
                shoulder, glove extended out front (+z). Spheres overlap so each
                arm reads as one connected limb, not two floating balls. */}
            {/* Left arm: sleeve + glove. */}
            <mesh castShadow position={[-0.23, 0.7, 0.3]}>
              <sphereGeometry args={[0.095, 14, 14]} />
              <meshStandardMaterial color={TUX.jacket} roughness={0.5} metalness={0.05} />
            </mesh>
            <mesh castShadow position={[-0.2, 0.63, 0.46]}>
              <sphereGeometry args={[0.085, 14, 14]} />
              <meshStandardMaterial color={TUX.gloves} roughness={0.7} />
            </mesh>
            {/* Right arm: sleeve + glove. */}
            <mesh castShadow position={[0.23, 0.7, 0.3]}>
              <sphereGeometry args={[0.095, 14, 14]} />
              <meshStandardMaterial color={TUX.jacket} roughness={0.5} metalness={0.05} />
            </mesh>
            <mesh castShadow position={[0.2, 0.63, 0.46]}>
              <sphereGeometry args={[0.085, 14, 14]} />
              <meshStandardMaterial color={TUX.gloves} roughness={0.7} />
            </mesh>
          </>
        ) : (
          <>
            <mesh castShadow position={[-0.3, 0.62, 0.18]}>
              <sphereGeometry args={[0.09, 12, 12]} />
              <meshStandardMaterial color={bodyColor} roughness={0.7} />
            </mesh>
            <mesh castShadow position={[0.3, 0.62, 0.18]}>
              <sphereGeometry args={[0.09, 12, 12]} />
              <meshStandardMaterial color={bodyColor} roughness={0.7} />
            </mesh>
          </>
        )}

        {/* Tuxedo (croupier only): white shirt front + lapels + black bowtie.
            NB: the body capsule has radius 0.28, so every tux piece sits at
            z ≳ 0.30 — PROUD of the chest surface — or it would be buried inside
            the capsule and invisible. */}
        {isCroupier && (
          <group>
            {/* White dress-shirt panel down the chest. A subtle emissive keeps it
                reading as a bright shirt even in the dim dealer zone — kept well
                below the bloom luminance threshold (1.0) so it never glows. */}
            <mesh position={[0, 0.66, 0.3]}>
              <boxGeometry args={[0.15, 0.46, 0.05]} />
              <meshStandardMaterial
                color={TUX.shirt}
                roughness={0.55}
                metalness={0}
                emissive={TUX.shirt}
                emissiveIntensity={0.35}
              />
            </mesh>
            {/* Jacket lapels: two thin angled strips flanking the shirt. */}
            <mesh position={[-0.13, 0.72, 0.31]} rotation={[0, 0, 0.32]}>
              <boxGeometry args={[0.08, 0.34, 0.04]} />
              <meshStandardMaterial color={TUX.jacket} roughness={0.45} metalness={0.05} />
            </mesh>
            <mesh position={[0.13, 0.72, 0.31]} rotation={[0, 0, -0.32]}>
              <boxGeometry args={[0.08, 0.34, 0.04]} />
              <meshStandardMaterial color={TUX.jacket} roughness={0.45} metalness={0.05} />
            </mesh>
            {/* Bowtie at the collar (just under the chin): two angled wings + a
                center knot, proud of the neck so it reads from across the table. */}
            <mesh position={[-0.08, 0.99, 0.33]} rotation={[0, 0, -0.5]}>
              <boxGeometry args={[0.13, 0.09, 0.04]} />
              <meshStandardMaterial color={TUX.bowtie} roughness={0.5} />
            </mesh>
            <mesh position={[0.08, 0.99, 0.33]} rotation={[0, 0, 0.5]}>
              <boxGeometry args={[0.13, 0.09, 0.04]} />
              <meshStandardMaterial color={TUX.bowtie} roughness={0.5} />
            </mesh>
            <mesh position={[0, 0.99, 0.35]}>
              <boxGeometry args={[0.05, 0.055, 0.05]} />
              <meshStandardMaterial color={TUX.bowtie} roughness={0.5} />
            </mesh>
          </group>
        )}
      </group>

      {/* Name label — object-anchored, so drei <Text> (3D) is appropriate here. */}
      <Text
        position={[0, 1.55, 0]}
        rotation={[0, -facing, 0]}
        fontSize={0.16}
        color={isCroupier ? palette.gold1 : palette.cream}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.006}
        outlineColor={palette.backdrop}
      >
        {name}
      </Text>
    </group>
  );
}

export function Blobs() {
  const roster = useGameStore((s) => s.server.roster);
  const resolution = useGameStore((s) => s.server.resolution);
  const result = useGameStore((s) => s.server.result);

  const players = useMemo<readonly RosterEntry[]>(
    () => roster.filter((r) => r.role === 'player'),
    [roster],
  );
  const croupier = useMemo(() => roster.find((r) => r.role === 'croupier'), [roster]);
  // The 11 fixed painted betting spots. Players occupy a CENTERED subset of them
  // (assignSeatIndices) so 2..11 players spread evenly across the near curve
  // instead of clustering at one end, and each still lands on a painted box.
  const seats = useMemo(() => computeSeats(PLAYER_SEAT_COUNT), []);
  const seatIndices = useMemo(() => assignSeatIndices(players.length), [players.length]);

  const reactionFor = (id: string): Reaction => {
    const winnerId = result?.winnerId ?? resolution?.winnerId;
    const loserId = result?.worstLoserId ?? resolution?.worstLoserId;
    if (winnerId === id) return 'win';
    if (loserId === id) return 'lose';
    return 'idle';
  };

  void fonts;

  return (
    <group>
      {players.map((p, i) => {
        const slot = seatIndices[i] ?? i;
        const seat = seats[slot];
        if (!seat) return null;
        return (
          <Blob
            key={p.id}
            seat={seat}
            name={p.name}
            isCroupier={false}
            reaction={reactionFor(p.id)}
            phaseOffset={i * 1.3}
            color={PLAYER_COLORS[slot % PLAYER_COLORS.length]!}
          />
        );
      })}
      {croupier && (
        <Blob
          key={croupier.id}
          seat={CROUPIER}
          name={`${croupier.name} (House)`}
          isCroupier
          reaction={reactionFor('__house__')}
          phaseOffset={0}
          color={TUX.jacket}
        />
      )}
    </group>
  );
}
